import React, { useEffect, useState } from 'react';
import api from '../../config/api';
import './AdminContactMessages.css';

const AdminContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
  }, [unreadOnly]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20
      });
      if (unreadOnly) {
        params.append('unread_only', 'true');
      }

      const response = await api.get(`/admin/contact-messages?${params}`);
      setMessages(response.data?.messages || []);
      setPagination(response.data?.pagination || { page: 1, pages: 1 });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/admin/contact-messages/unread-count');
      setUnreadCount(response.data?.count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleMarkAsRead = async (id, isRead) => {
    try {
      await api.put(`/admin/contact-messages/${id}/read`, { is_read: !isRead });
      await fetchMessages();
      await fetchUnreadCount();
    } catch (error) {
      alert('Failed to update message status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await api.delete(`/admin/contact-messages/${id}`);
      await fetchMessages();
      await fetchUnreadCount();
    } catch (error) {
      alert('Failed to delete message');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
    fetchMessages();
  };

  useEffect(() => {
    fetchMessages();
  }, [pagination.page]);

  if (loading && messages.length === 0) {
    return <div className="loading">Loading messages...</div>;
  }

  return (
    <div className="admin-contact-messages">
      <div className="page-header">
        <h1>Contact Messages</h1>
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount} unread</span>
        )}
      </div>

      <div className="filters">
        <label>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => {
              setUnreadOnly(e.target.checked);
              setPagination({ ...pagination, page: 1 });
            }}
          />
          Show unread only
        </label>
      </div>

      <div className="messages-table">
        {messages.length === 0 ? (
          <p>No messages found.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Message</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(message => (
                <tr key={message.id} className={!message.is_read ? 'unread' : ''}>
                  <td>{message.name}</td>
                  <td>
                    <a href={`mailto:${message.email}`}>{message.email}</a>
                  </td>
                  <td>
                    <div className="message-preview">
                      {message.message.length > 100 
                        ? `${message.message.substring(0, 100)}...` 
                        : message.message}
                    </div>
                  </td>
                  <td>{new Date(message.created_at).toLocaleString()}</td>
                  <td>
                    <span className={`status ${message.is_read ? 'read' : 'unread'}`}>
                      {message.is_read ? 'Read' : 'Unread'}
                    </span>
                  </td>
                  <td>
                    <button 
                      onClick={() => setSelectedMessage(message)} 
                      className="btn-small"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => handleMarkAsRead(message.id, message.is_read)}
                      className="btn-small"
                    >
                      {message.is_read ? 'Mark Unread' : 'Mark Read'}
                    </button>
                    <button 
                      onClick={() => handleDelete(message.id)}
                      className="btn-small btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            disabled={pagination.page === 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={pagination.page === pagination.pages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {selectedMessage && (
        <div className="message-modal">
          <div className="modal-content">
            <h2>Message Details</h2>
            <button onClick={() => setSelectedMessage(null)} className="close-btn">×</button>
            
            <div className="message-details">
              <div className="detail-row">
                <strong>Name:</strong>
                <span>{selectedMessage.name}</span>
              </div>
              <div className="detail-row">
                <strong>Email:</strong>
                <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
              </div>
              <div className="detail-row">
                <strong>Date:</strong>
                <span>{new Date(selectedMessage.created_at).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <strong>Status:</strong>
                <span className={`status ${selectedMessage.is_read ? 'read' : 'unread'}`}>
                  {selectedMessage.is_read ? 'Read' : 'Unread'}
                </span>
              </div>
              <div className="detail-row full-width">
                <strong>Message:</strong>
                <div className="message-text">{selectedMessage.message}</div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  handleMarkAsRead(selectedMessage.id, selectedMessage.is_read);
                  setSelectedMessage({ ...selectedMessage, is_read: !selectedMessage.is_read });
                }}
                className="btn btn-primary"
              >
                {selectedMessage.is_read ? 'Mark as Unread' : 'Mark as Read'}
              </button>
              <button
                onClick={() => {
                  handleDelete(selectedMessage.id);
                  setSelectedMessage(null);
                }}
                className="btn btn-danger"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedMessage(null)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContactMessages;
